// Page scaffolding shared by the feature pages: router tabs (TabsMenu), the list header (search left, filters and the
// primary action right; stacked on xs), and the error state legacy never showed (IMPROVE-BO-01).
import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Banner, CountBadge, TabBar } from '@radish/ui-web';
import { useActiveLocation } from '../app/location.tsx';
import { mayOpen } from '../app/page-permissions.ts';
import { pathFor, type AppPath } from '../router-paths.ts';
import { MATERIAL_WEST } from './material-icons.tsx';
import type { CallFailure } from '../data/api.tsx';

interface PageTab {
  readonly label: string;
  readonly to: AppPath;
  /** Things waiting on that tab, as a red count beside its label (Radish Direct gate 18: Online Orders). */
  readonly count?: number | undefined;
}

export function PageTabs({ label, tabs: all }: { readonly label: string; readonly tabs: readonly PageTab[] }) {
  const { slug, can } = useActiveLocation();
  // A tab the role cannot open is never shown (legacy hides its gated tabs; a shown one would lead to Access Denied).
  const tabs = all.filter((t) => mayOpen(t.to, can));
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The current tab is the one whose path is the longest match: a tab's path may extend another's (Business Profile's
  // Branding under Location Info), and only one tab is ever current.
  const matches = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const current = tabs.map((t) => pathFor(t.to, slug)).filter(matches).sort((x, y) => y.length - x.length)[0];
  return (
    <TabBar
      label={label}
      items={tabs.map((t) => ({ id: t.to, label: t.label, active: pathFor(t.to, slug) === current }))}
      // The router's own active state (aria-current, data-status) follows the same rule: an inactive tab matches exactly.
      renderTab={(item, className) => (
        <Link to={pathFor(item.id as AppPath, slug)} activeOptions={{ exact: !item.active }} className={className} aria-current={item.active ? 'page' : undefined}>
          {item.label}
          <CountBadge count={tabs.find((t) => t.to === item.id)?.count} className="ml-2" />
        </Link>
      )}
    />
  );
}

/**
 * `settings`: the page's legacy header is a SettingsHeader (components/layouts/SettingsHeader.vue: POS Devices, Printers,
 * Employee Breaks, Dining Options, Tender Types). Without it the header is PageHeaderAlternate (below). With it, legacy's
 * three Quasar bands:
 * - under 600 (xs) both levels stack `column-reverse`: the actions first, last one on top (Add, then Activate, then Search),
 *   the description under them;
 * - 600 to 1023 (sm) the groups sit side by side 30 px apart, top-aligned. The description's basis is its whole text on one
 *   line, so the actions' group (`50vw`, grow) is squeezed to its minimum: the search (a bare q-input, 177 px, shrinking to
 *   a 120 px floor: 177 at 1023, B2057) plus the buttons' one-line widths, and the description takes the rest (236 px at
 *   620 on POS Devices).
 *   Legacy's group is that wide too, but inside it Quasar shrinks the button instead of the search, so a long label wraps
 *   out of its 40 px box ("Activate a Device", B1019); Next's buttons keep their one-line width and the search gives way;
 * - from 1024 (md) 20 px apart, the description's group takes the free width with its text centred, the actions top-aligned,
 *   the search 177 px (list pages' PageHeaderAlternate search is 180, B984).
 *
 * Without it, legacy's PageHeaderAlternate (components/layouts/PageHeaderAlternate.vue, the list pages' header: every plain
 * ListHeader ports one): under 600 (xs) both levels stack `column-reverse` 10 px apart, so the end group's last action is
 * on top and the start group's last control above its first (Employees: Add Employee, Status, Search); from 600 (sm) the
 * groups sit in a row, start at the left and end at the right (B1121: Next kept source order and stacked below 640). The
 * rows leave `align-items` at its default, stretch, so a shorter control is as tall as the row's tallest and its content
 * stays where its own box puts it (B1147: Next centred them); nothing sits between the groups but justify-content.
 *
 * `wrap` (Accounts, B1146): from 600 the start group and then the end group wrap onto new rows 10 px apart, the end group
 * kept right, where legacy's rows don't wrap: Accounts' search, Reseller and Last drawer activity (180 + 180 + 240) and
 * Create Account need 752 px, so from 600 to about 800 legacy clips Create Account off the window's edge (B1200).
 */
export function ListHeader({ start, end, settings = false, wrap = false }: { readonly start?: ReactNode; readonly end?: ReactNode; readonly settings?: boolean; readonly wrap?: boolean }) {
  // Legacy's SettingsHeader puts 10 px between its controls, side by side and stacked alike (B812).
  const outer = settings
    ? 'mb-4 flex flex-col-reverse gap-2.5 min-[600px]:flex-row min-[600px]:justify-between min-[600px]:gap-x-[30px] min-[1024px]:gap-x-5'
    : `mb-4 flex flex-col-reverse gap-2.5 min-[600px]:flex-row min-[600px]:justify-between ${wrap ? 'min-[600px]:flex-wrap min-[600px]:gap-x-0' : 'min-[600px]:gap-0'}`;
  const first = settings
    ? 'flex flex-col-reverse gap-2.5 min-[600px]:flex-row min-[600px]:max-[1024px]:[&>*]:max-w-none min-[1024px]:flex-1 min-[1024px]:items-center'
    : `flex flex-col-reverse gap-2.5 min-[600px]:flex-row${wrap ? ' min-[600px]:flex-wrap' : ''}`;
  const second = settings
    ? 'flex flex-col-reverse gap-2.5 min-[600px]:flex-row min-[600px]:justify-end min-[600px]:[&>button]:shrink-0 min-[600px]:[&>button]:whitespace-nowrap min-[600px]:max-[1024px]:w-[50vw] min-[600px]:max-[1024px]:grow min-[600px]:max-[1024px]:[&>input]:w-[120px] min-[600px]:max-[1024px]:[&>input]:basis-[177px] min-[1024px]:[&>input]:w-[177px]'
    : `flex flex-col-reverse gap-2.5 min-[600px]:flex-row min-[600px]:justify-end${wrap ? ' min-[600px]:ml-auto' : ''}`;
  return (
    <div className={outer} data-testid="list-header">
      <div data-testid="list-header-start" className={first}>{start}</div>
      <div data-testid="list-header-end" className={second}>{end}</div>
    </div>
  );
}

/**
 * A page Save that spans the page under 600 px (legacy BaseButton `full` or `full-width` on domStore.isMobile, under
 * 599.99 px). Every other Button is as wide as its label there and 120 px at least from 600, by its own sizing (B1938).
 */
export const PHONE_FULL_WIDTH = 'max-[600px]:w-full';

/**
 * Legacy UiLink (components/ui/UiLink.vue), the "← Schedule Templates" and "← ALL ACCOUNTS" back links: a flex row, the
 * label 14 px medium in --q-text-tertiary (`text-font-tertiary text-weight-medium`; text-3 keeps that hue at AA contrast),
 * after Material `west` at 1.5rem (24 px) with q-mr-sm (8 px). `caps` callers add `uppercase`. No hover colour, as legacy.
 */
export const UI_LINK = 'inline-flex cursor-pointer items-center text-[14px] font-medium text-text-3';

export function UiLinkIcon() {
  return <svg viewBox="0 0 24 24" width={24} height={24} fill="currentColor" aria-hidden className="mr-2 shrink-0" data-testid="ui-link-icon"><path d={MATERIAL_WEST} /></svg>;
}

export function LoadError({ error }: { readonly error: CallFailure | null }) {
  return (
    <div className="py-6" data-testid="load-error">
      <Banner tone="offline">{error?.message ?? 'Something went wrong. Please try again.'}</Banner>
    </div>
  );
}
